import {
	ReactElement,
	useCallback,
	useEffect,
	useMemo,
	useState
} from "react";
import { localize } from "client_legacy/ndaw/culture";
import {
	AppAppearanceModel,
	AppModel,
	AppStoreModel,
	AppStorePermissionsClientModel,
	getAppAppearance,
	getAppStoreModel,
	getAppStorePermissions,
	getMainAppAppearance,
	SpaceListItem,
} from "client/Api/Apps/AppStore/appStore";
import { AppType } from "client/Api/Apps/common";
import {
	Dialog,
	FormattedText,
	TabsNavigationItemData,
} from "client/Components";
import { AppStore, AppStoreFilterConfiguration } from "./AppStore";
import { AddNewApp } from "./AddNewApp";
import { AppStoreEvent, publish } from "client_legacy/Apps/AppStore/events";
import { UnreachableCaseError } from "client_legacy/ndaw/errors";
import {
	isCancelledError
} from "client_legacy/ndaw/cancellation";
import { HttpRequestError } from "client/Api/httpClient";
import { allAppsCategory } from "./CategoryFilter";
import { newGuid } from "client_legacy/ndaw/guid";
import { RequestApp } from "./RequestApp";
import {
	AlertManager,
	useAlerts,
	useOpenDialog,
	useStableImperativeCallback
} from "client/Hooks";
import { ensureNotNil, isNotNil } from "client/Misc/utils";
import { AddTiles } from "./AddTiles/AddTiles";
import { CreateNewTile } from "./CreateNewTile";
import {
	MediaQueryKind,
	useWorkspaceMediaQuery,
} from "../../../Hooks/useWorkspaceMediaQuery";
import { NoPermissions } from "./NoPermissions/NoPermissions";
import { SpaceKind } from "client/Api/Workspace/workspace";
import { GoToSharedSpace } from "./GoToSharedSpace/GoToSharedSpace";
import { RootView } from "./RootView";
import { AppTarget, EditAppDialogContent } from "./EditApp/EditAppDialogContent";
import { useWorkspaceControllerStore, WorkspaceController } from "client/Features/Workspace/useWorkspaceControllerStore";

export type AppStoreViewKind =
	| "loading"
	| "store"
	| "add-new"
	| "request"
	| "add-new-tile"
	| "create-new-tile"
	| "create-shortcut"
	| "no-permissions"
	| "go-to-shared"
	| "edit-app";

export type InitialAppStoreView = {
	kind: "add-new-tile";
	targetTileGroupId: number;
} | {
	kind: "edit-app";
	installedAppId: number;
};

export type AppStoreDialogProps = {
	onClose: () => void;
	initialView?: InitialAppStoreView;
};

export const AppStoreDialog = (props: AppStoreDialogProps): ReactElement => {
	const {
		onClose: onCloseFromProps,
		initialView
	} = props;

	const workspaceController: OptionalModel<WorkspaceController> = useWorkspaceControllerStore(x => x.controller);

	const [busyCounter, setBusyCounter] = useState<number>(0);
	const [view, setView] = useState<AppStoreViewKind>("loading");

	const [hasError, setHasError] = useState<boolean>(false);
	const [permissions, setPermissions] = useState<AppStorePermissionsClientModel>();
	const [viewModel, setViewModel] = useState<AppStoreModel>({ apps: [], newApps: undefined });
	const [reload, setReload] = useState<boolean>(false);
	const [filterConfiguration, setFilterConfiguration] =
		useState<AppStoreFilterConfiguration>({
			searchTerm: {
				value: "",
				version: newGuid(),
			},
			access: [],
			category: allAppsCategory,
		});

	const isRootView: boolean = view === "add-new-tile"
		|| view === "request"
		|| view === "store"
		|| view === "go-to-shared";

	const [editAppState, setEditAppState] = useState<EditAppState | null>(null);
	const [isAppModifiedOrDisabled, setIsAppModifiedOrDisabled] = useState<boolean>(false);
	const onClose: () => void = useStableImperativeCallback(onCloseFromProps);

	const hasPersonalEditableSpace: boolean = useMemo(
		(): boolean => {
			return permissions?.editableSpaces
				.some((space) => space.kind === SpaceKind.Personal)
					?? false;
		},
		[permissions]
	);

	const hasEditableSharedSpace: boolean = useMemo(
		(): boolean => {
			return permissions?.editableSpaces
				.some((space) => space.kind === SpaceKind.Shared)
					?? false;
		},
		[permissions]
	);

	const alertManager: AlertManager = useAlerts();
	const isMobile: boolean = useWorkspaceMediaQuery(MediaQueryKind.Mobile);

	const activeSpaceId: number | null = workspaceController.isSet
		? workspaceController.value.activeSpaceId
		: null;

	const targetSpace = useMemo(
		(): SpaceListItem | null => {
			if (!permissions) {
				return null;
			}

			const {
				editableSpaces
			} = permissions;

			let space: SpaceListItem | undefined = editableSpaces.find(({ id }) => id === activeSpaceId);

			if (!space) {
				space = editableSpaces.find(
					({ kind }) => kind === SpaceKind.Personal
				);
			}

			return space ?? null;
		},
		[activeSpaceId, permissions]
	);

	const tabs: readonly TabsNavigationItemData<AppStoreViewKind>[] = useMemo(
		() => {
			const result: TabsNavigationItemData<AppStoreViewKind>[] = [];

			const hasAccessToTheManagementPart: boolean =
				Boolean(permissions?.canCreateApps) ||
				Boolean(permissions?.canEditAtleastOneApp);

			if (view === "loading" || view === "add-new") {
				return result;
			}

			if (
				initialView?.kind === "add-new-tile"
				|| targetSpace
				&& !isMobile
			) {
				result.push({
					text: localize("_AddTilesToWorkspace"),
					value: "add-new-tile",
					icon: "plus",
					trackingId: "app-store-tab|add-new-tiles"
				});
			} else if (
				hasEditableSharedSpace
				&& !workspaceController.isSet
				&& !isMobile
			) {
				result.push({
					text: localize("_AddTilesToWorkspace"),
					value: "go-to-shared",
					icon: "plus",
					trackingId: "app-store-tab|go-to-shared"
				});
			}

			if (permissions?.canRequestAppAccess) {
				result.push({
					text: localize("_RequestApps"),
					icon: "circle-question",
					value: "request",
					trackingId: "app-store-tab|request-apps"
				});
			}

			if (hasAccessToTheManagementPart) {
				result.push({
					text: localize("_ManageApps"),
					icon: "gear",
					value: "store",
					trackingId: "app-store-tab|manage-apps"
				});
			}

			return result;
		},
		[
			hasEditableSharedSpace,
			initialView,
			isMobile,
			permissions,
			targetSpace,
			view,
			workspaceController.isSet
		]
	);

	const setIsBusy = useCallback(
		(value: boolean): void => {
			setBusyCounter((x) => {
				const newValue: number = x + (value ? 1 : -1);

				if (newValue < 0) {
					throw Error("There are more callbacks for hiding the busy indicator than showing it.");
				}

				return newValue;
			});
		},
		[]
	);

	const initialNavigationHandler = useStableImperativeCallback(
		(permissions: AppStorePermissionsClientModel, viewModel: AppStoreModel | undefined): void => {
			if (initialView?.kind === "edit-app") {
				const target: AppModel | undefined = viewModel?.apps
					.find(
						x => x.installedAppId === initialView.installedAppId
					);

				if (isNotNil(target) && isNotNil(target.installedAppId)) {
					editAppHandler(
						{
							kind: "installed-app",
							app: {
								...target,
								installedAppId: target.installedAppId
							}
						},
						false
					);

					return;
				}

				setView("store");
				return;
			}

			if (
				initialView?.kind === "add-new-tile"
				|| (
					(targetSpace || hasPersonalEditableSpace) && !isMobile
				)
			) {
				setView("add-new-tile");
				return;
			}

			if (
				!workspaceController.isSet
				&& hasEditableSharedSpace
				&& !isMobile
			) {
				setView("go-to-shared");
				return;
			}

			if (
				permissions.canCreateApps ||
				permissions.canEditAtleastOneApp
			) {
				setView("store");
				return;
			}

			if (permissions.canRequestAppAccess) {
				setView("request");
				return;
			}

			setView("no-permissions");
		}
	);

	useEffect(
		() => {
			if (!permissions) {
				return;
			}

			const handlePermissionsChange = async (signal: AbortSignal): Promise<void> => {
				try {
					setIsBusy(true);

					let viewModel: AppStoreModel | undefined;
					if (
						permissions.canCreateApps ||
						permissions.canEditAtleastOneApp
					) {
						viewModel = await getAppStoreModel(signal);

						setViewModel(viewModel);
					}

					initialNavigationHandler(permissions, viewModel);

					setHasError(false);
					setIsBusy(false);
				} catch (e) {
					setIsBusy(false);
					if (isCancelledError(e)) {
						return;
					}

					console.error(e);

					alertManager.showError(
						localize(
							e instanceof HttpRequestError && e.statusCode === 403
								? "_YouDoNotHavePermissionForThat"
								: "_GeneralErrorMessage"
						)
					);

					setHasError(true);

					if (
						permissions.canCreateApps ||
						permissions.canEditAtleastOneApp
					) {
						setView("store");
					} else if (permissions.canRequestAppAccess) {
						setView("request");
					} else {
						onClose();
					}
				}
			};

			const cancellation = new AbortController();
			handlePermissionsChange(cancellation.signal);

			return () => {
				cancellation.abort();
			};
		},
		[
			alertManager,
			initialNavigationHandler,
			onClose,
			permissions,
			setIsBusy
		]
	);

	useEffect(
		() => {
			async function load(token: AbortSignal): Promise<void> {
				let data: AppStorePermissionsClientModel | null = null;

				setIsBusy(true);

				try {
					data = await getAppStorePermissions(token);

					setPermissions(data);

					setHasError(false);
					setIsBusy(false);
				} catch (e) {
					setIsBusy(false);
					if (isCancelledError(e)) {
						return;
					}
					console.error(e);
					alertManager.showError(
						localize(
							e instanceof HttpRequestError && e.statusCode === 403
								? "_YouDoNotHavePermissionForThat"
								: "_GeneralErrorMessage"
						)
					);

					setHasError(true);

					if (!data) {
						onClose();
					}
				}
			}

			const cancellation = new AbortController();
			load(cancellation.signal);
			return () => cancellation.abort();
		},
		[
			alertManager,
			onClose,
			setIsBusy,
			reload
		]
	);

	const getUpdatedAppModel = async (app: AppModel): Promise<AppModel> => {
		try {
			const appearance: AppAppearanceModel = await (app.installedAppId
				? getAppAppearance(app.installedAppId)
				: getMainAppAppearance(app.mainAppId));

			const updatedApp: AppModel = {
				...app,
				name: appearance.name,
				color: appearance.color,
				imageDisplayInfo: appearance.imageDisplayInfo,
				hasMaintenanceConfigured: appearance.hasMaintenanceConfigured,
				hasConditionalAccessConfigured:
					appearance.hasConditionalAccessConfigured,
				hasOwnerPermissionsConfigured:
					appearance.hasOwnerPermissionsConfigured,
				tags: appearance.tags
			};

			return updatedApp;
		} catch (e) {
			console.error(e);
			alertManager.showError(localize("_GeneralErrorMessage"));
			return app;
		}
	};

	const appUpdatedHandler = async (app: AppModel): Promise<void> => {
		const updatedAppModel: AppModel = await getUpdatedAppModel(app);

		setIsAppModifiedOrDisabled(true);

		setViewModel((x) => {
			const apps: AppModel[] = [...x.apps];

			let index: number;
			if (updatedAppModel.mainAppId) {
				index = apps.findIndex(
					(x) => x.mainAppId === updatedAppModel.mainAppId
				);
			} else if (updatedAppModel.installedAppId) {
				index = apps.findIndex(
					(x) => x.installedAppId === updatedAppModel.installedAppId
				);
			} else {
				throw new Error("No way of finding the app");
			}

			if (index === -1) {
				throw new Error(
					`Could not find app '${JSON.stringify(updatedAppModel)}'`
				);
			}

			apps.splice(index, 1, updatedAppModel);

			return {
				...x,
				apps,
			};
		});
	};

	const appDeletedHandler = (installedAppId: number): void => {
		setIsAppModifiedOrDisabled(true);

		setViewModel((x) => {
			const apps: AppModel[] = [...x.apps];
			const index: number = apps.findIndex(
				(x) => x.installedAppId === installedAppId
			);

			if (index === -1) {
				throw new Error(`Could not find app '${installedAppId}'`);
			}

			apps.splice(index, 1);

			return {
				...x,
				apps,
			};
		});
	};

	const refreshWorkspaceHandler = useCallback(
		async (): Promise<void> => {
			if (!workspaceController.isSet) {
				return;
			}

			await workspaceController.value
				.refresh();
		},
		[workspaceController]
	);

	const switchWorkspaceToEditModeHandler = useCallback(
		(): void => {
			if (!workspaceController.isSet) {
				return;
			}

			workspaceController.value
				.switchToEditMode();
		},
		[workspaceController]
	);

	const closeHandler = useCallback(
		(): void => {
			if (isAppModifiedOrDisabled) {
				refreshWorkspaceHandler();
			}

			onClose();
		},
		[
			isAppModifiedOrDisabled,
			onClose,
			refreshWorkspaceHandler
		]
	);

	const editAppHandler = (
		target: AppTarget,
		activateWhoHasAccessTab: boolean
	): void => {
		setEditAppState({
			target,
			activateWhoHasAccessTab
		});
		setView("edit-app");
	};

	const handleBackClick = useCallback(
		(value: AppStoreViewKind) => () => {
			setView(value);
		},
		[]
	);

	const handleCreateTileClick = useCallback(
		() => {
			setView("create-new-tile");
		},
		[]
	);

	const editAppAfterSaveHandler = useCallback(
		(
			id: number,
			type: AppType,
			name: string,
			target: AppTarget,
			appUpdatedHandler: (app: AppModel) => Promise<void>
		): void => {
			switch (target.kind) {
				case "installed-app":
					setIsAppModifiedOrDisabled(true);
					target.app.installedAppId = id;
					appUpdatedHandler(target.app);
					break;
				case "new-app-from-main":
					publish(AppStoreEvent.AppEnabled, {
						id,
						type,
					});
					target.app.installedAppId = id;
					appUpdatedHandler(target.app);
					break;
				case "new-environment-app":
					alertManager.showSuccess(
						<FormattedText
							text={localize("_AppNameIsAddedToTheAppStore")}
							thingsToReplace={[
								{ what: "{{AppName}}", filler: name },
							]}
						/>
					);

					setFilterConfiguration({
						category: allAppsCategory,
						access: [],
						searchTerm: {
							value: "",
							version: newGuid(),
						},
					});
					setView("store");
					setReload((x) => !x);
					break;
				default:
					throw new UnreachableCaseError(target);
			}
		},
		[alertManager]
	);

	const editAppBackHandler = useCallback(
		() => {
			setView("store");
			setEditAppState(null);
		},
		[]
	);

	const targetTileGroupId: number | null = initialView?.kind === "add-new-tile"
		? initialView.targetTileGroupId
		: null;

	return (
		<Dialog
			size="large"
			hasBusyIndicator={busyCounter > 0}
			trackingId="app-store-dialog"
		>
			{isRootView && (
				<RootView
					activeTab={view}
					tabs={tabs}
					onClose={closeHandler}
					onChangeView={setView}
				>
					{view === "store" && (
						<AppStore
							model={viewModel}
							permissions={ensureNotNil(permissions)}
							filterConfiguration={filterConfiguration}
							onFilterConfigurationChange={setFilterConfiguration}
							onBusyChange={setIsBusy}
							onChangeView={setView}
							onEditApp={editAppHandler}
							onAppUpdated={appUpdatedHandler}
							onAppDeleted={appDeletedHandler}
							onClose={closeHandler}
							hasLoadingAppsError={hasError}
							onRetry={() => setReload(x => !x)}
						/>
					)}
					{view === "request" && (
						<RequestApp onClose={closeHandler} />
					)}
					{view === "add-new-tile" && (
						<AddTiles
							targetSpace={targetSpace}
							targetTileGroupId={targetTileGroupId}
							activeSpaceId={activeSpaceId}
							onCreateTileClick={handleCreateTileClick}
							onClose={closeHandler}
							setIsBusy={setIsBusy}
							onRefreshWorkspace={refreshWorkspaceHandler}
							onSwitchWorkspaceToEditMode={switchWorkspaceToEditModeHandler}
						/>
					)}
					{view === "go-to-shared" && (
						<GoToSharedSpace onClose={closeHandler} />
					)}
				</RootView>
			)}

			{view === "add-new" && (
				<AddNewApp
					apps={ensureNotNil(viewModel.newApps)}
					onEditApp={editAppHandler}
					onBack={handleBackClick("store")}
					onClose={closeHandler}
				/>
			)}

			{view === "create-new-tile" && (
				<CreateNewTile
					targetTileGroupId={targetTileGroupId}
					targetSpace={targetSpace}
					activeSpaceId={activeSpaceId}
					onClose={closeHandler}
					onBack={handleBackClick("add-new-tile")}
					onIsBusyChanged={setIsBusy}
					onRefreshWorkspace={refreshWorkspaceHandler}
					onSwitchWorkspaceToEditMode={switchWorkspaceToEditModeHandler}
				/>
			)}

			{view === "no-permissions" && (
				<NoPermissions onClose={closeHandler} />
			)}

			{(view === "edit-app" && editAppState) && (
				<EditAppDialogContent
					target={editAppState.target}
					activateWhoHasAccessTab={editAppState.activateWhoHasAccessTab}
					onAfterSave={(id, type, name) => {
						editAppAfterSaveHandler(
							id,
							type,
							name,
							editAppState.target,
							appUpdatedHandler
						);
					}}
					onBack={editAppBackHandler}
					onClose={closeHandler}
					onIsBusyChanged={setIsBusy}
				/>
			)}
		</Dialog>
	);
};
AppStoreDialog.displayName = "AppStoreDialog";

type EditAppState = {
	target: AppTarget;
	activateWhoHasAccessTab: boolean;
};

export type ConfirmationDialogInitialData = Omit<
	AppStoreDialogProps,
	"onClose"
>;

export function useAppStoreDialog(): [
	element: ReactElement | null,
	open: (props: ConfirmationDialogInitialData) => Promise<void>
] {
	return useOpenDialog(
		(props, onClose) => (
			<AppStoreDialog
				{...props}
				onClose={onClose}
			/>
		)
	);
}
